import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import * as XLSX from 'xlsx';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [editingBox, setEditingBox] = useState(null);
  const [editingTruck, setEditingTruck] = useState(null);
  const [showBoxForm, setShowBoxForm] = useState(false);
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [showBoxOptions, setShowBoxOptions] = useState(false);
  const [algorithmResult, setAlgorithmResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [outputFiles, setOutputFiles] = useState([]);
  const [csvData, setCsvData] = useState(null);
  const [outputFolderPath, setOutputFolderPath] = useState('');
  const [plotJsonData, setPlotJsonData] = useState(null); // <-- for interactive 3D from .json
  const inputRefs = useRef({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [showTruckOptions, setShowTruckOptions] = useState(false);
  const truckDropdownRef = useRef(null);
  const [truckSummary, setTruckSummary] = useState([]);

  const [uploadType, setUploadType] = useState(null); // 'box' or 'truck'
  const [fileDataPreview, setFileDataPreview] = useState(null); // parsed data preview
  const [uploadError, setUploadError] = useState('');


  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // --- NEW: Modal state for drop input flow ---
  const [showModal, setShowModal] = useState(false);
  // const [isFindingTruck, setIsFindingTruck] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1 -> ask number of drops, 2 -> show per-drop inputs
  const [numDrops, setNumDrops] = useState(1);
  // dropData: { 1: { box_id1: qty, box_id2: qty, ... }, 2: {...} }
  const [dropData, setDropData] = useState({});

  const handleUploadClick = (type) => {
    setUploadType(type);
    setFileDataPreview(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = null; // reset file input
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/octet-stream'
    ];

    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(csv|xls|xlsx)$/i)) {
      setUploadError('Unsupported file format. Please upload a CSV or Excel file.');
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const data = event.target.result;
      let workbook;

      if (file.name.match(/\.(xls|xlsx)$/i)) {
        try {
          workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          validateAndPreviewData(jsonData);
        } catch (err) {
          console.error(err);
          setUploadError("Error parsing Excel file. Make sure it’s a valid Excel document.");
        }
      } else {
        try {
          const lines = data.split('\n').filter(line => line.trim());
          if (lines.length === 0) {
            setUploadError("Empty CSV file.");
            return;
          }

          workbook = XLSX.read(data, { type: 'string' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          validateAndPreviewData(parsedData);
        } catch (err) {
          console.error(err);
          setUploadError("Error parsing CSV file. Make sure it’s properly formatted.");
        }
      }
    };

    if (file.name.match(/\.(xls|xlsx)$/i)) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsText(file);
    }
  };

  const ErrorModal = ({ message, onClose }) => {
    if (!message) return null;

    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <div className="bg-red-500 text-white rounded-lg p-6 max-w-sm text-center shadow-lg">
          <h3 className="font-semibold text-lg mb-3">Upload Error</h3>
          <p className="text-sm">{message}</p>
          <button
            onClick={onClose}
            className="mt-4 bg-white text-red-600 px-4 py-2 rounded hover:bg-gray-100 font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  const validateAndPreviewData = (data) => {

    // ⛔ Check if file is completely empty
    if (!data || data.length === 0) {
      setUploadError("Uploaded file is empty or unreadable.");
      setFileDataPreview([]);
      return;
    }

    // Normalize keys (in case of extra spaces)
    const normalize = (str) => str.trim().toLowerCase();

    const headers = Object.keys(data[0]).map(normalize);

    const requiredColumns = uploadType === 'box'
      ? ['box_id', 'length', 'width', 'height', 'weight']
      : ['truck_id', 'length', 'width', 'height', 'weight'];

    const missingCols = requiredColumns.filter(
      (col) => !headers.includes(col)
    );

    if (missingCols.length > 0) {
      setUploadError(`Missing required columns: ${missingCols.join(', ')}`);
      setFileDataPreview([]);
      return;
    }

    const numericFields = ['length', 'width', 'height', 'weight'];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      for (const field of numericFields) {
        const value = row[field];

        if (value === '' || value === null || value === undefined) {
          setUploadError(`Row ${i + 1}: '${field}' is empty.`);
          setFileDataPreview([]);
          return;
        }

        if (isNaN(parseFloat(value))) {
          setUploadError(`Row ${i + 1}: '${field}' should be a number. Got "${value}".`);
          setFileDataPreview([]);
          return;
        }
      }
    }

    // ✅ All validations passed
    setUploadError('');
    setFileDataPreview(data);
    console.log("✅ Data is valid and ready for preview.");
  };


  const handleSubmitUpload = async () => {
    if (!selectedFile || !uploadType) {
      setUploadError('Please select a file and upload type.');
      return;
    }

    try {
      const formData = new FormData();
      formData.set('type', uploadType);
      formData.set('file', selectedFile);

      await axios.post(`${backendUrl}/api/form_input_bulk`, formData);

      if (uploadType === 'box') fetchBoxes();
      else if (uploadType === 'truck') fetchTrucks();

      setFileDataPreview(null);
      setUploadType(null);
      setSelectedFile(null);
      setUploadError('');
    } catch (error) {
      console.error(error);
      setUploadError('Failed to upload data. Please try again.');
    }
  };

  const [boxForm, setBoxForm] = useState({
    box_id: '',
    length: '',
    width: '',
    height: '',
    weight: ''
  });
  const [truckForm, setTruckForm] = useState({
    truck_id: '',
    length: '',
    width: '',
    height: '',
    weight: ''
  });

  const [showBoxSelector, setShowBoxSelector] = useState(false);
  const [boxSelections, setBoxSelections] = useState([]);

  useEffect(() => {
    fetchBoxes();
    fetchTrucks();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setShowBoxOptions(false);
      }
      if (
        truckDropdownRef.current && !truckDropdownRef.current.contains(event.target)
      ) {
        setShowTruckOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // Fetch and parse the .json file for Plotly when outputFiles change
  useEffect(() => {
    setPlotJsonData(null);
    const jsonFile = outputFiles.find(f => f.endsWith('.json'));
    if (jsonFile) {
      axios
        .get(`${backendUrl}/api/get_output_file`, {
          params: { folder: outputFolderPath, filename: jsonFile },
          responseType: 'text'
        })
        .then(res => {
          try {
            const plotJson = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            setPlotJsonData(plotJson);
          } catch (err) {
            setPlotJsonData(null);
            setError("Could not parse 3D layout JSON.");
          }
        })
        .catch(() => {
          setPlotJsonData(null);
          setError("Could not fetch 3D layout JSON.");
        });
    }
  }, [outputFiles]);

  const handleLogout = () => {
    navigate('/login');
  };

  const fetchBoxes = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/get_boxes`);
      setBoxes(res.data || []);
      setBoxSelections(res.data.map(box => ({ ...box, quantity: 0 })));
    } catch (error) {
      console.error('Error fetching boxes:', error);
    }
  };

  // -------------------------
  // Shared processing flow (reused from handleRunAlgorithm)
  // After a successful /process response we run list_outputs and fetch CSV/JSON like before.
  // -------------------------
  const callProcessWithPayload = async (payload) => {
    // payload shape:
    // { boxes: [ {box_id,length,width,height,weight,drop_id,quantity}, ... ], trucks: [ ... ] }
    setLoading(true);
    setError('');
    setAlgorithmResult(null);
    setOutputFiles([]);
    setCsvData(null);
    setTruckSummary([]);

    try {
      const res = await axios.post(`${backendUrl}/api/process`, payload);
      const data = res.data;
      if (data.status !== "Done") {
        throw new Error(data.message || "Algorithm error");
      }

      setAlgorithmResult(data);
      setOutputFolderPath(data.folder);

      if (data.truck_summary) setTruckSummary(data.truck_summary);

      // list outputs
      const listRes = await axios.post(`${backendUrl}/api/list_outputs`, {
        folder: data.folder
      });
      if (listRes.data.success) {
        const files = listRes.data.files;
        setOutputFiles(files);

        // find CSV file and fetch it
        const csvFile = files.find(f => f.endsWith('.csv'));
        if (csvFile) {
          try {
            const csvRes = await axios.get(`${backendUrl}/api/get_output_file`, {
              params: { folder: data.folder, filename: csvFile }
            });
            if (typeof csvRes.data === "string") {
              const lines = csvRes.data.split('\n').filter(l => l.trim());
              if (lines.length > 1) {
                const headers = lines[0].split(',');
                const rows = lines.slice(1).map(line => {
                  const cols = line.split(',');
                  const obj = {};
                  headers.forEach((h, i) => obj[h] = cols[i]);
                  return obj;
                });
                setCsvData({ headers, rows });
              } else {
                setCsvData(null);
              }
            } else {
              setCsvData(null);
            }
          } catch (err) {
            setCsvData(null);
            setError("Could not fetch CSV results.");
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
      // ensure modal hidden if anything left
      setShowModal(false);
      setModalStep(1);
    }
  };

  const fetchTrucks = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/get_trucks`);
      setTrucks(res.data || []);
    } catch (error) {
      console.error('Error fetching trucks:', error);
    }
  };

  const handleBoxSubmit = async () => {
    const { box_id, length, width, height, weight } = boxForm;

    // 🚫 Basic Validation
    if (!box_id || length <= 0 || width <= 0 || height <= 0 || weight <= 0) {
      alert("Please enter valid values for all fields. Dimensions and weight must be positive.");
      return;
    }

    try {
      if (editingBox) {
        await axios.put(`${backendUrl}/api/update_box/${editingBox}`, { ...boxForm });
      } else {
        await axios.post(`${backendUrl}/api/form_input`, { type: 'box', ...boxForm });
      }
      setShowBoxForm(false);
      setShowBoxOptions(false);
      setBoxForm({ box_id: '', length: '', width: '', height: '', weight: '' });
      setEditingBox(null);
      fetchBoxes();
    } catch (error) {
      console.error('Error submitting box:', error);
    }
  };


  const handleTruckSubmit = async () => {
    const { truck_id, length, width, height, weight } = truckForm;

    // 🚫 Basic Validation
    if (!truck_id || length <= 0 || width <= 0 || height <= 0 || weight <= 0) {
      alert("Please enter valid values for all fields. Dimensions and weight must be positive.");
      return;
    }

    try {
      if (editingTruck) {
        await axios.put(`${backendUrl}/api/update_truck/${editingTruck}`, { ...truckForm });
      } else {
        await axios.post(`${backendUrl}/api/form_input`, { type: 'truck', ...truckForm });
      }
      setShowTruckForm(false);
      setTruckForm({ truck_id: '', length: '', width: '', height: '', weight: '' });
      setEditingTruck(null);
      fetchTrucks();
    } catch (error) {
      console.error('Error submitting truck:', error);
    }
  };

  const handleBoxDelete = async (box_id) => {
    try {
      await axios.delete(`${backendUrl}/api/delete_box/${box_id}`);
      fetchBoxes();
    } catch (error) {
      console.error('Error deleting box:', error);
    }
  };

  const handleTruckDelete = async (truck_id) => {
    try {
      await axios.delete(`${backendUrl}/api/delete_truck/${truck_id}`);
      fetchTrucks();
    } catch (error) {
      console.error('Error deleting truck:', error);
    }
  };

  const handleRunAlgorithm = async () => {
    const selectedBoxes = boxSelections.filter(b => b?.quantity > 0);
    if (!selectedBoxes.length) {
      alert("Select at least one box.");
      return;
    }
    const payload = { boxes: selectedBoxes, trucks };
    await callProcessWithPayload(payload);
    setShowBoxSelector(false);
  };

  // -------------------------
  // NEW: Modal flow handlers
  // -------------------------
  const openFindTruckModal = () => {
    // Reset modal state and open
    setNumDrops(1);
    setDropData({});
    setModalStep(1);
    setShowModal(true);
  };

  const initDropData = (n) => {
    const dd = {};
    for (let i = 1; i <= n; i++) {
      dd[i] = {};
      // initialize every available box id to 0
      boxes.forEach(b => {
        dd[i][b.box_id] = 0;
      });
    }
    setDropData(dd);
  };

  const handleNumDropsConfirm = () => {
    const n = parseInt(numDrops, 10);
    if (!n || n <= 0) {
      alert("Please enter a valid positive integer for drop locations.");
      return;
    }
    initDropData(n);
    setModalStep(2);
  };

  const handleQtyChange = (dropIndex, boxId, val) => {
    const q = val === '' ? '' : Math.max(0, parseInt(val || 0, 10));
    setDropData(prev => {
      const copy = { ...prev };
      if (!copy[dropIndex]) copy[dropIndex] = {};
      copy[dropIndex][boxId] = q;
      return copy;
    });
  };


  const handleModalSubmit = async () => {
    // Build boxes array excluding zero or empty quantities
    const payloadBoxes = [];
    for (let d = 1; d <= numDrops; d++) {
      const mapping = dropData[d] || {};
      for (const [boxId, qty] of Object.entries(mapping)) {
        const q = Number(qty || 0);
        if (!q || q <= 0) continue; // exclude zeros
        // find box metadata from boxes list
        const boxMeta = boxes.find(b => b.box_id === boxId);
        if (!boxMeta) continue;
        payloadBoxes.push({
          box_id: boxMeta.box_id,
          length: Number(boxMeta.length),
          width: Number(boxMeta.width),
          height: Number(boxMeta.height),
          weight: Number(boxMeta.weight),
          drop_id: Number(d),
          quantity: Number(q)
        });
      }
    }

    if (payloadBoxes.length === 0) {
      alert("No boxes selected for any drop. Please enter quantities before submitting.");
      return;
    }

    // Close modal immediately and show spinner (loading)
    setShowModal(false);
    setModalStep(1);
    setLoading(true);

    // Build payload object expected by backend
    const payload = {
      boxes: payloadBoxes,
      trucks
    };

    console.log("Payload being sent to backend:", payload);

    // call process
    try {
      await callProcessWithPayload(payload);
    } catch (err) {
      // callProcessWithPayload handles errors & sets error state
      console.error("Error during process call:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBoxEdit = (box) => {
    setEditingBox(box.box_id);
    setBoxForm({ ...box });
    setShowBoxForm(true);
    setShowBoxOptions(false);
  };

  const handleTruckEdit = (truck) => {
    setEditingTruck(truck.truck_id);
    setTruckForm({ ...truck });
    setShowTruckForm(true);
  };

  const renderTable = (data, columns, isBox) => (
    // Add overflow-x-auto only for small and medium screens
    <div className="overflow-x-auto sm:overflow-x-auto md:overflow-x-auto lg:overflow-x-visible rounded">
      <table className="w-full text-left border-separate border-spacing-y-2 text-sm text-white min-w-[600px] sm:min-w-full">
        <thead className="text-orange-400">
          <tr>
            {columns.map(col => (
              <th key={col} className="px-4 py-2 whitespace-nowrap">{col.toUpperCase()}</th>
            ))}
            <th className="px-4 py-2 whitespace-nowrap">ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="text-center py-4 text-gray-500">No data available</td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr key={idx} className="bg-[#262626] rounded-lg">
                {columns.map(col => (
                  <td key={col} className="px-4 py-2 whitespace-nowrap">{row[col]}</td>
                ))}
                <td className="px-4 py-2 text-sm space-x-3 whitespace-nowrap">
                  <button onClick={() => isBox ? handleBoxEdit(row) : handleTruckEdit(row)} className="text-orange-400 hover:underline">Edit</button>
                  <button onClick={() => isBox ? handleBoxDelete(row.box_id) : handleTruckDelete(row.truck_id)} className="text-red-500 hover:underline">Delete</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const boxOrder = ['box_id', 'length', 'width', 'height', 'weight'];
  const truckOrder = ['truck_id', 'length', 'width', 'height', 'weight'];

  const renderForm = (form, setForm, handleSubmit, order) => (
    <div className="mt-4 space-y-3">
      {order.map(key => (
        <input
          key={key}
          placeholder={key.toUpperCase()}
          className="w-full bg-[#1C1C1C] border border-orange-500 text-white p-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
          value={form[key]}
          ref={el => inputRefs.current[key] = el}
          onChange={e => {
            const val = e.target.value;
            setForm(prevForm => ({
              ...prevForm,
              [key]: val,
            }));
            setTimeout(() => {
              if (inputRefs.current[key]) {
                inputRefs.current[key].focus();
              }
            }, 0);
          }}
        />
      ))}
      <button
        onClick={handleSubmit}
        className="w-full py-2 rounded bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition"
      >
        Submit
      </button>
    </div>
  );

  const Card = ({ title, data, columns, showForm, setShowForm, form, setForm, handleSubmit, isBox }) => (
    <div className="relative bg-[#393e46] rounded-xl shadow-inner border border-[#2E2E2E] p-6 max-w-full overflow-x-hidden">
      <div className="flex justify-between items-center mb-4 relative">
        <h2 className="text-lg font-semibold text-orange-400">{title}</h2>

        {isBox ? (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => {
                setShowBoxOptions(!showBoxOptions);
                setShowBoxForm(false);
                setEditingBox(null);
              }}
              className="w-8 h-8 bg-orange-500 text-black rounded-full text-xl font-bold flex items-center justify-center hover:bg-orange-600"
            >
              +
            </button>

            {showBoxOptions && (
              <div className="absolute right-0 mt-2 w-40 bg-[#2E2E2E] rounded-md shadow-lg z-10 border border-orange-500">
                <button
                  className="block w-full px-4 py-2 text-left text-white hover:bg-orange-600"
                  onClick={() => {
                    setShowBoxForm(true);
                    setShowBoxOptions(false);
                    setEditingBox(null);
                    setBoxForm({ box_id: '', length: '', width: '', height: '', weight: '' });
                  }}
                >
                  Add Manually
                </button>
                <button
                  className="block w-full px-4 py-2 text-left text-white hover:bg-orange-600"
                  onClick={() => {
                    handleUploadClick('box');
                    setShowBoxOptions(false);
                  }}
                >
                  Upload File
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="relative" ref={truckDropdownRef}>
            <button
              onClick={() => {
                setShowTruckOptions(!showTruckOptions);
                setShowTruckForm(false);
                setEditingTruck(null);
              }}
              className="w-8 h-8 bg-orange-500 text-black rounded-full text-xl font-bold flex items-center justify-center hover:bg-orange-600"
            >
              +
            </button>

            {showTruckOptions && (
              <div className="absolute right-0 mt-2 w-40 bg-[#2E2E2E] rounded-md shadow-lg z-10 border border-orange-500">
                <button
                  className="block w-full px-4 py-2 text-left text-white hover:bg-orange-600"
                  onClick={() => {
                    setShowTruckForm(true);
                    setShowTruckOptions(false);
                    setEditingTruck(null);
                    setTruckForm({ truck_id: '', length: '', width: '', height: '', weight: '' });
                  }}
                >
                  Add Manually
                </button>
                <button
                  className="block w-full px-4 py-2 text-left text-white hover:bg-orange-600"
                  onClick={() => {
                    handleUploadClick('truck');
                    setShowTruckOptions(false);
                  }}
                >
                  Upload File
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wrap table + form inside a div with overflow scroll on small devices */}
      <div className="overflow-x-auto sm:overflow-x-auto md:overflow-x-auto lg:overflow-x-visible rounded">
        {renderTable(data, columns, isBox)}
        {showForm && renderForm(
          form,
          setForm,
          handleSubmit,
          form === boxForm ? boxOrder : truckOrder
        )}
      </div>
    </div>
  );


  return (
    <div className="min-h-screen bg-[#212121] text-white px-3 py-10 font-sans relative">
      {/* 🔹 Navigation Bar */}
      <nav className="w-full bg-[#2E2E2E] px-4 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 shadow-md fixed top-0 left-0 z-20">
        {/* First Line: Admin Title */}
        <div>
          <h1 className="text-xl sm:text-xl text-center font-bold text-orange-400">Admin Dashboard</h1>
        </div>

        {/* Second Line: Buttons */}
        <div className="flex gap-3 justify-between mt-2 text-sm flex-wrap">
          <button
            onClick={() => window.open("https://routeopt.outworx.com/", "_blank")}
            className="bg-orange-500 hover:bg-orange-700 text-white px-3 py-1.5 rounded shadow-md transition"
          >
            Best Route
          </button>

          <a
            href="http://122.176.145.246:3005/"
            target="_blank"
            rel="noreferrer"
            className="text-white underline px-3 py-1.5 rounded shadow-md transition hover:text-orange-300"
          >
            See Live Video
          </a>

          <button
            onClick={handleLogout}
            className="bg-orange-500 hover:bg-orange-700 text-white px-3 py-1.5 rounded shadow-md transition"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* 🔹 Main Container */}
      <div className="flex flex-col items-center space-y-8 mt-20 w-full max-w-[900px] mx-auto">

        {/* Truck Details */}
        <div className="w-full bg-[#393e46] rounded-xl shadow-inner sm:border border-[#2E2E2E] p-3 sm:p-6 overflow-x-auto text-xs sm:text-sm">
          <Card
            title="Available Trucks (Units are in feet and kg)"
            data={trucks}
            columns={['truck_id', 'length', 'width', 'height', 'weight', 'created_at']}
            showForm={showTruckForm}
            setShowForm={setShowTruckForm}
            form={truckForm}
            setForm={setTruckForm}
            handleSubmit={handleTruckSubmit}
            isBox={false}
          />
        </div>
        {/* Box Details */}
        <div className="w-full bg-[#393e46] rounded-xl shadow-inner sm:border border-[#2E2E2E] p-3 sm:p-6 overflow-x-auto text-xs sm:text-sm">
          <Card
            title="Box Details (Units are in feet and kg)"
            data={boxes}
            columns={['box_id', 'length', 'width', 'height', 'weight', 'created_at']}
            showForm={showBoxForm}
            setShowForm={setShowBoxForm}
            form={boxForm}
            setForm={setBoxForm}
            handleSubmit={handleBoxSubmit}
            isBox={true}
          />
        </div>

        {/* Find Best Fit Truck Button (opens modal) */}
        <div className="flex justify-start w-full mt-2 sm:mt-6">
          <div>
            <button
              onClick={openFindTruckModal}
              disabled={loading}
              className={`relative overflow-hidden backdrop-blur-md border rounded-full font-semibold py-2 px-8 cursor-pointer transition duration-300 ease-in-out flex items-center gap-3 justify-center
    ${loading
                  ? "bg-[#FF8A0C]/40 border-[#FF8A0C]/30 text-white cursor-not-allowed"
                  : "bg-[#FF8A0C]/20 border-[#FF8A0C]/30 text-[#FF8A0C] hover:bg-[#FF8A0C]/30 hover:border-[#FF8A0C]/50 hover:-translate-y-1 hover:shadow-[0_15px_35px_rgba(255,138,12,0.2)] hover:text-white active:-translate-y-0.5"
                }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 010 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z" />
                  </svg>
                  Finding the best truck...
                </>
              ) : (
                "Find Best Fit Truck"
              )}
            </button>

          </div>
        </div>
      </div>

      {/* ---------------------------
          Modal: Drop locations & quantities
         --------------------------- */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-[#2E2E2E] w-[95%] max-w-4xl rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-orange-400">Find Best Fit Truck</h3>
              <button onClick={() => { setShowModal(false); setModalStep(1); }} className="text-white text-2xl">&times;</button>
            </div>

            {/* Step 1: ask number of drops */}
            {modalStep === 1 && (
              <div>
                <p className="text-sm text-gray-300 mb-2">Enter number of drop locations</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    value={numDrops}
                    onChange={(e) => setNumDrops(Math.max(1, parseInt(e.target.value || 1, 10)))}
                    className="bg-[#1C1C1C] border border-orange-500 px-3 py-2 rounded text-white w-32"
                  />
                  <button
                    onClick={handleNumDropsConfirm}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-semibold"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: show per-drop box quantities */}
            {modalStep === 2 && (
              <div className="max-h-[70vh] overflow-y-auto">
                <p className="text-sm text-gray-300 mb-2">For each drop location, enter quantity of each box (leave 0 to exclude).</p>
                <p className="text-sm text-gray-300 mb-2">Drop 1 will be delivered first.</p>

                {/* Render each drop */}
                {Array.from({ length: numDrops }).map((_, di) => {
                  const dropIndex = di + 1;
                  const mapping = dropData[dropIndex] || {};
                  return (
                    <div key={dropIndex} className="mb-6 border border-gray-700 rounded p-3 bg-[#262626]">
                      <h4 className="text-orange-300 font-semibold mb-2">Drop {dropIndex}</h4>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {boxes.map(box => (
                          <div key={box.box_id} className="flex items-center justify-between bg-[#1C1C1C] p-2 rounded">
                            <div className="flex items-center gap-3">
                              <div className="text-sm">{box.box_id}</div>
                              <div className="text-xs text-gray-400">({box.length}×{box.width}×{box.height})</div>
                            </div>
                            <input
                              type="number"
                              min={0}
                              placeholder="0"
                              value={mapping[box.box_id] === undefined ? 0 : mapping[box.box_id]}
                              onChange={(e) => handleQtyChange(dropIndex, box.box_id, e.target.value)}
                              className="w-20 bg-[#262626] text-white px-2 py-1 rounded border border-orange-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-end gap-3 mt-2">
                  <button onClick={() => { setModalStep(1); }} className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600">Back</button>
                  <button
                    onClick={handleModalSubmit}
                    className="px-4 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                  >
                    Submit & Find Best Fit Truck
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ErrorModal message={uploadError} onClose={() => setUploadError('')} />

      {/* Data preview modal */}
      {fileDataPreview && fileDataPreview.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-[#2E2E2E] text-white rounded-2xl shadow-lg w-[90%] max-w-[900px] p-6 relative">
            <h2 className="text-xl font-semibold text-orange-400 mb-4 text-center">
              {uploadType === "box" ? "Box Data Preview" : "Truck Data Preview"}
            </h2>

            <div className="overflow-x-auto max-h-[400px] border border-gray-600 rounded-lg">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-700">
                    {Object.keys(fileDataPreview[0]).map((header) => (
                      <th key={header} className="px-3 py-2 border border-gray-600 text-sm uppercase">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fileDataPreview.map((row, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? "bg-gray-800" : "bg-gray-700"}`}>
                      {Object.values(row).map((val, i) => (
                        <td key={i} className="px-3 py-1 border border-gray-600 text-sm">{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {uploadError && <p className="text-red-500 text-center mt-2">{uploadError}</p>}

            <div className="flex justify-end mt-6 space-x-3">
              <button onClick={() => { setFileDataPreview([]); setUploadType(null); setUploadError(""); }} className="bg-gray-700 hover:bg-gray-800 px-4 py-2 rounded-lg font-semibold">Cancel</button>
              <button onClick={handleSubmitUpload} disabled={uploadError !== ""} className={`px-4 py-2 rounded-lg font-semibold ${uploadError ? "bg-gray-500 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"}`}>Submit Upload</button>
            </div>

            <button onClick={() => { setFileDataPreview([]); setUploadType(null); setUploadError(""); }} className="absolute top-3 right-4 text-gray-400 hover:text-white text-2xl">&times;</button>
          </div>
        </div>
      )}

      <input type="file" accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileChange} />


      {/* 🔹 CSV Output */}
      {csvData && (
        <div className="bg-[#2E2E2E] p-2 sm:p-4 rounded-lg text-white shadow w-full max-w-[900px] mx-auto mt-6 text-xs sm:text-sm">
          <h3 className="text-orange-400 font-semibold mb-2 text-sm sm:text-base">Utilization Report</h3>

          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-left border-collapse border border-gray-600 min-w-[600px]">
              <thead>
                <tr className="bg-gray-700">
                  {csvData.headers.map((h) => (
                    <th
                      key={h}
                      className="px-1 py-1 sm:px-3 sm:py-2 border border-gray-600 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvData.rows.map((row, i) => (
                  <tr key={i} className="border border-gray-600 even:bg-gray-800">
                    {csvData.headers.map((h) => (
                      <td
                        key={h}
                        className={`border border-gray-600 whitespace-nowrap ${h === 'truck_id' || h.toLowerCase().includes('utilization')
                          ? 'px-1 sm:px-2 py-1 sm:py-2 text-[10px] sm:text-sm'
                          : 'px-2 sm:px-3 py-1 sm:py-2'
                          }`}
                      >
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Output images & videos (unchanged) */}
      {outputFiles.length > 0 && (
        <div className="w-full max-w-[900px] mx-auto mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 px-2">
          {outputFiles.filter(f => f.endsWith('.png')).map(img => (
            <div key={img} className="bg-[#2E2E2E] rounded-lg p-3 sm:p-4 text-white shadow flex flex-col items-center justify-center">
              <h3 className="text-orange-400 font-semibold mb-2 text-sm">Image Output</h3>
              <img src={`${backendUrl}/api/get_output_file?folder=${encodeURIComponent(outputFolderPath)}&filename=${encodeURIComponent(img)}`} alt="Output PNG" className="rounded max-w-full max-h-[300px] object-contain" />
            </div>
          ))}
          {outputFiles.filter(f => f.endsWith('.mp4')).map(video => (
            <div key={video} className="bg-[#2E2E2E] rounded-lg p-3 sm:p-4 text-white shadow flex flex-col items-center justify-center">
              <h3 className="text-orange-400 font-semibold mb-2 text-sm">Video Output</h3>
              <video controls className="w-full h-auto max-h-[300px] rounded">
                <source src={`${backendUrl}/api/get_output_file?folder=${encodeURIComponent(outputFolderPath)}&filename=${encodeURIComponent(video)}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ))}
        </div>
      )}

      {/* 3D Plot (unchanged) */}
      {plotJsonData && (
        <div className="bg-[#2E2E2E] mt-6 p-3 sm:p-4 rounded-lg text-white w-full max-w-[900px] mx-auto shadow overflow-x-auto">
          <h2 className="text-base sm:text-lg font-semibold text-orange-400 mb-2">3D Packing Layout:</h2>
          <Plot data={plotJsonData.data} layout={plotJsonData.layout} config={{ responsive: true }} style={{ width: "100%", height: "600px" }} />
        </div>
      )}

      {/* Error */}
      {error && <p className="mt-4 text-center text-red-400 text-sm">{error}</p>}
    </div>
  );
};

export default AdminPage;